import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Check, ChevronDown, ChevronUp, ChevronsUpDown, Settings2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import {
  flexRender, getCoreRowModel, getPaginationRowModel, getSortedRowModel, useReactTable,
  type ColumnDef, type PaginationState, type RowData, type SortingState, type VisibilityState
} from '@tanstack/react-table';
export type { ColumnDef } from '@tanstack/react-table';

declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> { numeric?: boolean }
}

export interface DataTableProps<T> {
  columns: ColumnDef<T, any>[];
  data: T[];
  emptyMessage?: string;
  error?: string;
  pageSize?: number;
  page?: number;
  totalPages?: number;
  sort?: string;
  onPageChange?: (page: number) => void;
  onSortChange?: (sort: string | undefined) => void;
  toolbar?: ReactNode;
}

export function DataTable<T>({ columns, data, emptyMessage = 'No results found.', error, pageSize = 5, page, totalPages, sort, onPageChange, onSortChange, toolbar }: DataTableProps<T>) {
  const [localSorting, setLocalSorting] = useState<SortingState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [density, setDensity] = useState<'comfortable' | 'compact'>('comfortable');
  const [localPagination, setLocalPagination] = useState<PaginationState>({ pageIndex: 0, pageSize });
  const controlledSorting: SortingState = sort ? [{ id: sort.replace(/^-/, ''), desc: sort.startsWith('-') }] : [];
  const pagination = onPageChange ? { pageIndex: Math.max((page ?? 1) - 1, 0), pageSize } : localPagination;
  const sorting = onSortChange ? controlledSorting : localSorting;
  const table = useReactTable({
    columns, data, state: { sorting, columnVisibility, pagination }, onSortingChange: (updater) => {
      const next = typeof updater === 'function' ? updater(sorting) : updater;
      if (onSortChange) { const first = next[0]; onSortChange(first ? `${first.desc ? '-' : ''}${first.id}` : undefined); } else setLocalSorting(next);
    },
    onColumnVisibilityChange: setColumnVisibility, onPaginationChange: (updater) => {
      const next = typeof updater === 'function' ? updater(pagination) : updater;
      if (onPageChange) onPageChange(next.pageIndex + 1); else setLocalPagination(next);
    },
    manualPagination: Boolean(onPageChange), manualSorting: Boolean(onSortChange),
    ...(totalPages === undefined ? {} : { pageCount: totalPages }),
    getCoreRowModel: getCoreRowModel(), getSortedRowModel: getSortedRowModel(), getPaginationRowModel: getPaginationRowModel()
  });
  if (error) return <div className="bv-empty" role="alert"><strong>Unable to load table</strong><span className="bv-muted">{error}</span></div>;
  return <div className="bv-surface">
    <div className="bv-table-tools">
      <div className="bv-table-tools__filters">{toolbar}</div>
      <div className="bv-table-tools__actions">
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button className="bv-button bv-button--quiet" type="button"><Settings2 aria-hidden="true" size={16} />Columns</button>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content className="bv-dropdown bv-column-menu" align="end" sideOffset={4}>
              {table.getAllLeafColumns().map((column) => {
                const label = typeof column.columnDef.header === 'string' ? column.columnDef.header : column.id;
                return <DropdownMenu.CheckboxItem
                  key={column.id}
                  className="bv-dropdown__item"
                  checked={column.getIsVisible()}
                  onCheckedChange={(value) => column.toggleVisibility(Boolean(value))}
                >
                  <span className="bv-dropdown__check" aria-hidden="true">{column.getIsVisible() ? <Check size={14} /> : null}</span>
                  <span>{label}</span>
                </DropdownMenu.CheckboxItem>;
              })}
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
        <button className="bv-button bv-button--quiet" type="button" onClick={() => setDensity((value) => value === 'comfortable' ? 'compact' : 'comfortable')}>Density: {density}</button>
      </div>
    </div>
    {table.getRowModel().rows.length === 0 ? <div className="bv-empty">{emptyMessage}</div> : <div className="bv-table-wrap"><table className={`bv-table${density === 'compact' ? ' bv-table--compact' : ''}`}>
      <thead>{table.getHeaderGroups().map((group) => <tr key={group.id}>{group.headers.map((header) => <th key={header.id} scope="col">
        {header.isPlaceholder ? null : header.column.getCanSort() ? <button type="button" onClick={header.column.getToggleSortingHandler()} aria-label={`Sort by ${String(header.column.columnDef.header)}`}>
          {flexRender(header.column.columnDef.header, header.getContext())}{header.column.getIsSorted() === 'asc' ? <ChevronUp aria-hidden="true" size={14} /> : header.column.getIsSorted() === 'desc' ? <ChevronDown aria-hidden="true" size={14} /> : <ChevronsUpDown aria-hidden="true" size={14} />}
        </button> : <span>{flexRender(header.column.columnDef.header, header.getContext())}</span>}
      </th>)}</tr>)}</thead>
      <tbody>{table.getRowModel().rows.map((row) => <tr key={row.id}>{row.getVisibleCells().map((cell) => <td key={cell.id} data-numeric={cell.column.columnDef.meta?.numeric ? 'true' : undefined}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>)}</tr>)}</tbody>
    </table></div>}
    <nav className="bv-pagination" aria-label="Table pagination">
      <button className="bv-button bv-button--quiet" type="button" onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()}>Previous</button>
      <span>Page {table.getState().pagination.pageIndex + 1} of {Math.max(table.getPageCount(), 1)}</span>
      <button className="bv-button bv-button--quiet" type="button" onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>Next</button>
    </nav>
  </div>;
}
